import { Col } from '../../cols/col.class';
import { BehaviorSubject } from 'rxjs';
import { LocalTable } from './table.class';
import { Ring } from '../../chain.class';
import { Casher } from '../../cash.class';
import { Searcher, SearchMode } from '../../../../components/searcher/searcher.component';
import { STUB_FN_FALSE } from '../../../../utils/stubs';

export class Filter<D extends object> extends Casher<FilterCash> implements Ring<D[]> {
   public input: BehaviorSubject<D[]>;
   public output = new BehaviorSubject<D[]>([]);
   readonly byCol = new Map<Col<D>, ColFilter<D>>();
   readonly global: GlobalFilter = {
      searchable: '',
      tags: new Set(),
   };


   constructor(
      private ctx: LocalTable<D>,
   ) {
      super({
         cellId: `Filter`,
         containerId: ctx.id,
         isActive: ctx.isCashable,
      });
      this.setColWithTagsRelation();
      this.apply();
   }


   public subscribe() {
      return this.input.subscribe(rows => {
         this.filterAll();
      });
   }


   private setColWithTagsRelation() {
      this.ctx.cols.controlable.forEach(col => {
         const title = col.cell.stringify;

         this.byCol.set(col, {
            tags: new Set(),
            delete: tag => this.removeTagFromCol(col, tag),
            searcher: new Searcher<D>({
               title,
               stream: this.output,
               isSelected: val => this.isSelected(col, title(val)),
               select: op => this.addTagToCol(col, new Tag(title(op), 'euqals')),
               customSelect: searchable => this.addTagToCol(col, new Tag(searchable, 'includes')),
            })
         });
      });
   }


   public addTagToCol(col: Col<D>, tag: Tag) {
      this.byCol.get(col).tags.add(tag);
      this.global.searchable = ``;
      this.filterAll();
   }


   public removeTagFromCol(col: Col<D>, tag: Tag) {
      this.byCol.get(col).tags.delete(tag);
      this.filterAll();
   }


   public getTagsByCol(col: Col<D>): Set<Tag> {
      return this.byCol.get(col).tags;
   }


   public getTagsByColArray(col: Col<D>): Tag[] {
      return Array.from(this.getTagsByCol(col).values());
   }


   public addGlobalTag() {
      let isNotAdded = true;

      for (const [col, filter] of this.byCol) {
         const existed = filter.searcher.tryTogetFromUnique(this.global.searchable);
         if (existed) {
            filter.searcher.ctx.select(existed);
            isNotAdded = false;
            break;
         }
      }

      if (isNotAdded) {
         this.global.tags.add({
            title: this.global.searchable,
            mode: 'includes'
         });
      }
      
      this.global.searchable = ``;
      this.filterAll();
   }


   public removeGlobalTag(tag: Tag) {
      this.global.tags.delete(tag);
      this.filterAll();
   }


   public searchInCols() {
      const { searchable, tags } = this.global;

      for (const [col, filter] of this.byCol) {
         filter.searcher.search(searchable);
      }
   }


   public clear() {
      for (const [col, filter] of this.byCol) {
         filter.tags = new Set();
      }
      this.global.tags = new Set();
      this.filterAll();
   }


   public filterAll() {
      const rows = this.input.getValue();
      const byGlobalTags = this.byGlobalTags(rows);
      const byColsTags = this.byColsTags(byGlobalTags);

      this.output.next(byColsTags);
   }


   private isSelected(col: Col<D>, title: string): boolean {
      return !!Array.from(this.byCol.get(col).tags.values()).find(tag => tag.title.toLowerCase() === title.toLowerCase());
   }


   private byColsTags(rows: D[]): D[] {
      const cols = this.ctx.cols.visible.filter(col => col.isControlable);
      let byColsTags: D[] = rows;


      for (const col of cols) {
         const getter = col.cell.stringify;
         const tags = this.getTagsByCol(col);

         tags.forEach(tag => {
            const comparer = this.getComparer(tag, getter);

            byColsTags = byColsTags.filter(comparer)
         });
      }

      return byColsTags;
   }


   private byGlobalTags(rows: D[]): D[] {
      const global = Array.from(this.global.tags.values());
      const cols = this.ctx.cols.visible.filter(col => col.isControlable);
      let byGlobalTags = rows;

      if (global.length) {
         byGlobalTags = byGlobalTags.filter((row, ind) => {
            let isInAnyCol = false;

            for (const col of cols) {
               const getter = col.cell.stringify;

               isInAnyCol = isInAnyCol || global.some(tag => {
                  const comparer = this.getComparer(tag, getter);

                  return comparer(row);
               });
            }

            return isInAnyCol;
         });
      }

      return byGlobalTags;
   }


   private getComparer(tag: Tag, getter: (op: object) => string) {
      let comparer: (op: D) => boolean = STUB_FN_FALSE;
      const searchable = tag.title.toLowerCase();
      const get = (row: D) => getter(row).toLowerCase();

      switch (tag.mode) {
         case 'includes': comparer = row => get(row).includes(searchable); break;
         case 'start': comparer = row => get(row).slice(0, searchable.length) === searchable; break;
         case 'end': comparer = row => get(row).slice(-searchable.length) === searchable; break;
         case 'euqals': comparer = row => get(row) === searchable; break;
         default: console.error(`Unknown search mode`);
      }

      return comparer;
   }


   public save(): FilterCash {
      return {
         globalTags: Array.from(this.global.tags),
         tagsByCol: Array.from(this.byCol.entries())
            .reduce((acc, [col, filter]) => {
               acc[col.id] = Array.from(filter.tags);
               return acc
            }, {})
      }
   }

   public restore({ globalTags, tagsByCol }) {
      this.global.tags = new Set(globalTags);

      for (const [ col, filter ] of this.byCol) {
         filter.tags = new Set(tagsByCol[col.id] || []);
      }
   }
}

export class Tag {
   constructor(
      public title: string,
      public mode: SearchMode,
   ) { }
}

export interface GlobalFilter {
   tags: Set<Tag>;
   searchable: string;
}

export interface ColFilter<D extends object> {
   tags: Set<Tag>;
   searcher: Searcher<D>;
   delete(tag: Tag): void;
}

export interface FilterCash {
   globalTags: Tag[];
   tagsByCol: {
      [colId: string]: Tag[];
   }
}
